<script setup lang="ts">
import { debounce, truncate } from 'lodash'
import AdicionarEventoDialog from '@/pages/admin/pesquisa-avancada/components/AdicionarEventoDialog.vue'
import FornecedorDetalhesDialog from '@/pages/admin/pesquisa-avancada/components/FornecedorDetalhesDialog.vue'
import { usePesquisaAvancada } from '@/pages/admin/pesquisa-avancada/store/usePesquisaAvancada'

const store = usePesquisaAvancada()

definePage({
  meta: {
    action: 'list',
    subject: 'pesquisa-avancada',
  },
})

const {
  searchText,
  searchBoxSuggest,
  suppliersInfinite,
  suppliers,
  suppliersTotal,
  suppliersPage,
  selectedSupplier,
  search,
  events,
  loading,
  dialog,
  handlerScrollKey,
  categoriesSupplier,
  categoryExperiences,
  categorySpaces,
} = storeToRefs(store)

const {
  fetchSearchBoxSuggest,
  clearSearchBoxSuggest,
  fetchCategories,
  fetchCategoriasEspacoEventos,
  fetchCategoriaExperiencia,
  fetchSearchSpaceEvents,
  fetchEvents,
  openDialogSupplier,
  openDialogAddEvent,
  selectEvent,
} = store

const onInput = debounce(value => {
  if (value)
    fetchSearchBoxSuggest(value)
  else
    clearSearchBoxSuggest()
}, 300)

const onSearch = () => {
  suppliers.value = []
  suppliersPage.value = 1
  handlerScrollKey.value = Date.now() + Math.random()
  fetchSearchSpaceEvents({ done: () => {} })
}

onMounted(async () => {
  await Promise.all([
    fetchCategories(),
    fetchCategoriasEspacoEventos(),
    fetchCategoriaExperiencia(),
    fetchEvents(),
  ])
})

onBeforeRouteLeave(() => {
  store.$reset()
})

const categories = computed(() => {
  if (search.value.category_type === 'suplicy')
    return categoriesSupplier.value
  if (search.value.category_type === 'space-events')
    return categorySpaces.value
  if (search.value.category_type === 'experience')
    return categoryExperiences.value
})
</script>

<template>
  <VCard class="mb-2">
    <VCardItem>
      <VRow class="pa-4 pa-sm-6 flex-column gap-2">
        <div
          class="d-flex align-center justify-end gap-1"
          style="flex: 1;"
        >
          <div class="w-16">
            <VTextField
              v-model="search.date"
              :disabled="search.category_type !== 'space-events'"
              type="date"
              variant="outlined"
              placeholder="Data"
              class="app-input"
              hide-details
              :rules="[requiredValidator]"
            />
          </div>
          <div style="flex: 1;">
            <VAutocomplete
              v-model="search.category_type"
              :items="[
                { slug: 'suplicy', descricao: 'Fornecedores' },
                { slug: 'space-events', descricao: 'Espaços para eventos' },
                { slug: 'experience', descricao: 'Experiências' },
              ]"
              variant="outlined"
              placeholder="Tipo de serviço"
              class="app-search"
              item-value="slug"
              item-title="descricao"
              no-data-text="Nenhuma tipo de serviço foi encontrado"
              hide-details
            />
          </div>
          <div style="flex: 1;">
            <VAutocomplete
              v-model="search.category_slug"
              :items="categories"
              variant="outlined"
              class="app-search"
              placeholder="Qual é a categoria?"
              item-value="slug"
              item-title="descricao"
              no-data-text="Nenhuma categoria foi encontrada"
              hide-details
              :rules="[requiredValidator]"
            />
          </div>
          <div class="w-25">
            <VMenu>
              <template #activator="{ props }">
                <VTextField
                  v-model="searchText"
                  variant="outlined"
                  placeholder="Qual a localização?"
                  class="app-input"
                  v-bind="props"
                  :loading="loading.searchBoxSuggest"
                  :rules="[requiredValidator]"
                  hide-details
                  @update:model-value="onInput"
                />
              </template>
              <VList>
                <VListItem
                  v-for="(item, index) in searchBoxSuggest"
                  :key="index"
                  @click="() => {
                    searchText = item.text
                    search.region = item.value
                  }"
                >
                  <VListItemTitle>{{ item.text }}</VListItemTitle>
                </VListItem>
                <VListItem
                  v-if="searchBoxSuggest.length === 0"
                  class="text-center"
                >
                  <VListItemTitle>Nenhuma localização foi encontrada</VListItemTitle>
                </VListItem>
              </VList>
            </VMenu>
          </div>
          <div class="w-15">
            <VTextField
              v-model="search.capacity"
              :disabled="search.category_type !== 'space-events'"
              type="number"
              variant="outlined"
              placeholder="Convidados"
              class="app-input"
              hide-details
              :rules="[requiredValidator]"
            />
          </div>
          <CDFButton
            color="primary"
            size="48"
            is-icon
            rounded="pill"
            icon="tabler-search"
            :loading="loading.searchSpaceEvents"
            @click="onSearch"
          />
        </div>
      </VRow>
    </VCardItem>
  </VCard>

  <div class="featured-companies">
    <VInfiniteScroll
      v-if="suppliers.length > 0"
      :key="handlerScrollKey"
      ref="suppliersInfinite"
      color="secondary"
      mode="manual"
      class="supplier-content flex-row flex-wrap my-2"
      @load="fetchSearchSpaceEvents"
    >
      <VCol
        v-for="(supplier, index) in suppliers"
        :key="index"
        cols="12"
        sm="6"
        md="4"
      >
        <VCard
          hover
          class="supplier-card h-100"
          elevation="2"
        >
          <VImg
            :src="supplier.destaque ?? 'https://placehold.co/1920x1080'"
            eager
            class="card-image supplier-image"
            aspect-ratio="16/9"
          />

          <VCardText class="d-flex flex-column pa-3">
            <h3 class="text-subtitle-1 font-weight-bold mb-2">
              {{ supplier.nome_fantasia }}
            </h3>

            <p class="text-body-2 text-grey-darken-1 mb-3">
              {{
                truncate(supplier.descricao, {
                  length: 150,
                  separator: ' ',
                })
              }}
            </p>

            <VSpacer />

            <div class="d-flex gap-2 w-100">
              <VBtn
                size="small"
                class="flex-grow-1"
                @click="openDialogSupplier(supplier)"
              >
                Ver Mais
              </VBtn>
              <VBtn
                color="success"
                size="small"
                class="flex-grow-1"
                @click="openDialogAddEvent(supplier)"
              >
                Adicionar
              </VBtn>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <template #load-more="{ props }">
        <VBtn
          v-if="suppliers.length !== suppliersTotal"
          v-bind="props"
          color="primary"
          width="150"
          variant="outlined"
          class="px-2 text-sm"
          size="sm"
        >
          Ver mais
        </VBtn>
      </template>
      <template #empty />
    </VInfiniteScroll>

    <VEmptyState
      v-else
      class="supplier-empty"
      icon="tabler-search"
      title="Nenhum resultado encontrado"
      size="80"
      color="primary"
      subtitle="Tente novamente com outras palavras-chave."
    />
  </div>

  <!-- Dialog de Detalhes do Fornecedor -->
  <FornecedorDetalhesDialog
    v-model="dialog.supplierDetail"
    :fornecedor="selectedSupplier"
    @fechar="dialog.supplierDetail = false"
  />

  <!-- Dialog de Adicionar ao Evento -->
  <AdicionarEventoDialog
    v-model="dialog.eventDetail"
    :eventos="events"
    @fechar="dialog.eventDetail = false"
    @selecionar-evento="selectEvent"
  />
</template>

<style lang="scss">
/* Estilos baseados no seu print */
.w-16 {
  inline-size: 16%;
}

.w-15 {
  inline-size: 15%;
}

.supplier-content {
  .v-infinite-scroll__side {
    display: none;
  }

  .v-infinite-scroll__side {
    display: block;
    inline-size: 100%;
    text-align: center;
  }
}

.supplier-empty {
  block-size: calc(100% - 25%);
}

.supplier-card {
  display: flex;
  overflow: hidden;
  flex-direction: column;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 15%);
    transform: translateY(-2px);
  }
}
</style>
